import BarChart from '@/components/charts/bar-chart'
import DoughnutChart from '@/components/charts/doughnut-chart'
import StackedBarChart from '@/components/charts/happiness-check-chart'
import LineChart from '@/components/charts/line-chart'
import DashboardCard from '@/components/dashboard-card'
import { useCampaignClient, useEndUsersDashboardClient } from '@/lib/api'
import { useQuery as useReactQuery } from '@tanstack/react-query'
import Select from 'react-select'

import BarChartHorizontal from '@/components/charts/bar-chart-horizontal'
import { format } from 'date-fns'
import { t } from 'i18next'
import { useTranslation } from 'react-i18next'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { hasHappinessCheck } from '../../../end-users/src/utils/happiness-check'

export default function Dashboard() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const campaignClient = useCampaignClient()
  const endUsersDashboardClient = useEndUsersDashboardClient()

  // Fetch campaigns using ts-rest client
  const { data: campaignsResponse, isLoading: loading } = useReactQuery({
    queryKey: ['dashboard-campaigns'],
    queryFn: () =>
      campaignClient.getAll.query({
        query: {
          itemsPerPage: '100', // Get enough campaigns for the dashboard
        },
      }),
  })

  // Extract campaigns from the response
  const data = campaignsResponse?.body
    ? {
        campaigns:
          (
            campaignsResponse.body as {
              data?: Array<{
                name: string
                id: string
                referralCampaign?: boolean
                happinessCheck?: unknown[] | null
                typeOfCampaign?: string
                postcardTemplate?: { type?: string }
              }>
            }
          )?.data || [],
      }
    : { campaigns: [] }
  const campaignId = useParams().campaignId || (data && data.campaigns[0]?.id)
  const transformedData =
    data &&
    data.campaigns.map((d: { name: string; id: string }) => {
      return { label: d.name, value: d.id }
    })
  const isReferral =
    data?.campaigns?.find((c) => c.id === campaignId)?.referralCampaign ?? false
  const showHappinessCheck = hasHappinessCheck(
    data?.campaigns?.find((c) => c.id === campaignId),
  )

  // Fetch end users data
  const { data: endUsersResponse, isLoading: endUsersLoading } = useReactQuery({
    queryKey: ['endUsersPerCampaign', campaignId],
    queryFn: async () =>
      await endUsersDashboardClient.getEndUsersPerCampaign.query({
        params: { campaignId: campaignId! },
      }),
    enabled: !!campaignId,
  })
  const endUsersData = { endUsersPerCampaign: endUsersResponse?.body as any }

  // Fetch incentives data
  const { data: incentivesResponse, isLoading: incentivesLoading } =
    useReactQuery({
      queryKey: ['incentivesPerCampaign', campaignId],
      queryFn: async () =>
        await endUsersDashboardClient.getIncentivesPerCampaign.query({
          params: { campaignId: campaignId! },
        }),
      enabled: !!campaignId,
    })
  const incentivesData = {
    incentivesPerCampaign: incentivesResponse?.body as any,
  }

  // Fetch campaign performance
  const {
    data: campaignPerformanceResponse,
    isLoading: campaignPerformanceLoading,
  } = useReactQuery({
    queryKey: ['campaignPerformance', campaignId],
    queryFn: async () =>
      await endUsersDashboardClient.getCampaignPerformance.query({
        params: { campaignId: campaignId! },
      }),
    enabled: !!campaignId,
  })
  const campaignPerformance = {
    campaignPerformance: campaignPerformanceResponse?.body as any,
  }

  // Fetch campaign performance over time
  const {
    data: campaignPerformanceOverTimeResponse,
    isLoading: campaignPerformanceOverTimeLoading,
  } = useReactQuery({
    queryKey: ['campaignPerformanceOverTime', campaignId],
    queryFn: async () =>
      await endUsersDashboardClient.getCampaignPerformanceOverTime.query({
        params: { campaignId: campaignId! },
      }),
    enabled: !!campaignId,
  })
  const campaignPerformanceOverTime = {
    campaignPerformanceOverTime:
      campaignPerformanceOverTimeResponse?.body as any,
  }

  // Fetch happiness check
  const { data: happinessCheckResponse, isLoading: happinessCheckLoading } =
    useReactQuery({
      queryKey: ['happinessCheck', campaignId],
      queryFn: async () =>
        await endUsersDashboardClient.getEndUsersHappinessCheck.query({
          params: { campaignId: campaignId! },
        }),
      enabled: !!campaignId,
    })
  const happinessCheckCount = {
    endUsersHappinessCheck: happinessCheckResponse?.body,
  }

  // Fetch vouchers info
  const { data: vouchersInfoResponse, isLoading: getVouchersInfoLoading } =
    useReactQuery({
      queryKey: ['vouchersInfo', campaignId],
      queryFn: async () =>
        await endUsersDashboardClient.getVouchersInfo.query({
          params: { campaignId: campaignId! },
        }),
      enabled: !!campaignId,
    })
  const getVouchersInfo = { getVouchersInfo: vouchersInfoResponse?.body }

  const getVoucherStatistics: any[] = Array.isArray(
    getVouchersInfo?.getVouchersInfo,
  )
    ? getVouchersInfo.getVouchersInfo.map(
        (incentive: {
          incentiveItemId: string
          incentiveItemName: string
          voucherCodes: { id: string; isUsed: boolean }[]
        }) => {
          const usedVouchers = incentive.voucherCodes
            .filter((voucher) => voucher.isUsed)
            .length.toString()
          const totalVouchers = incentive.voucherCodes.length.toString()
          return {
            label: `${incentive.incentiveItemName} ${usedVouchers}/${totalVouchers}`,
            data: usedVouchers,
          }
        },
      )
    : []

  const selectedCampaign = data?.campaigns?.find((c) => c.id === campaignId)
  const isOnlineCampaign = selectedCampaign?.typeOfCampaign === 'online'
  const endUsersPerCampaign =
    selectedCampaign?.postcardTemplate?.['type'] === 'flyer'
      ? (campaignPerformance?.campaignPerformance as any)?.general?.find(
          (c: { action: string; count: number }) => c.action === 'reached',
        )?.count
      : endUsersData?.endUsersPerCampaign?.general.length || 0

  if (loading) return <div>Loading...</div>
  if (
    endUsersLoading ||
    incentivesLoading ||
    campaignPerformanceLoading ||
    campaignPerformanceOverTimeLoading ||
    happinessCheckLoading ||
    getVouchersInfoLoading
  )
    return <div>Loading...</div>
  return (
    <div className="p-10">
      <div className="flex justify-between items-center gap-4 mb-4">
        <div className="flex gap-4 justify-start items-start">
          <h1 className="m-auto font-bold text-2xl leading-none">
            {t('dashboard_labels.dashboard')}
          </h1>
          {campaignId && (
            <span className="p-1 bg-green-60/25 text-green-500 rounded">
              {selectedCampaign?.name}
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          <NavLink
            to="/advanced-dashboard"
            className="text-primary hover:underline"
          >
            {t('dashboard_labels.view_customer_dashboards')}
          </NavLink>

          <Select
            defaultValue={transformedData?.find((c) => c.value === campaignId)}
            options={transformedData}
            onChange={(value) => value && navigate(`/dashboard/${value.value}`)}
            className="w-64"
          />
        </div>
      </div>
      {campaignId && (
        <>
          <h1 className="pt-4 text-2xl border-b border-ui">
            {t('dashboard_labels.general_campaign')}
          </h1>
          <div className="grid grid-cols-2 gap-6">
            {endUsersData?.endUsersPerCampaign?.general.length && (
              <DashboardCard
                title={t('dashboard_labels.campaign_overview')}
                description={t(
                  'dashboard_labels.since_the_beginning_of_the_campaign',
                )}
                children={
                  <BarChart
                    data={generalToChart(
                      campaignPerformance?.campaignPerformance?.general,
                      endUsersPerCampaign,
                      isOnlineCampaign,
                    )}
                    type="campaign"
                  />
                }
              />
            )}
            {incentivesData?.incentivesPerCampaign?.general && (
              <DashboardCard
                title={t('dashboard_labels.requested_incentives')}
                description={t('dashboard_labels.by_type')}
                children={
                  <BarChart
                    data={incentivesData?.incentivesPerCampaign?.general}
                    type="incentive"
                  />
                }
              />
            )}
            {campaignPerformanceOverTime?.campaignPerformanceOverTime?.general
              ?.data && (
              <DashboardCard
                title={t('dashboard_labels.campaign_performance')}
                description={t('dashboard_labels.over_time')}
                children={
                  <LineChart
                    data={campaignPerformanceOverTime?.campaignPerformanceOverTime?.general.data?.map(
                      (row: any) => {
                        // Removed unused variable
                        if (!row) return { label: '', data: '' }
                        return {
                          label: t(
                            `dashboard_labels.performance_by_time.general.${row.label}`,
                          ),
                          data: String(row.data?.[0] || ''),
                        }
                      },
                    )}
                    labels={
                      campaignPerformanceOverTime?.campaignPerformanceOverTime?.general.labels?.map(
                        (l: any) =>
                          (l && format(new Date(l), 'yyyy-MM-dd')) || '',
                      ) || []
                    }
                  />
                }
              />
            )}
            {incentivesData?.incentivesPerCampaign?.general && (
              <DashboardCard
                title={t('dashboard_labels.requested_incentives')}
                description={t('dashboard_labels.by_share')}
                children={
                  <DoughnutChart
                    data={incentivesData?.incentivesPerCampaign?.general}
                  />
                }
              />
            )}
          </div>
          {isReferral && (
            <>
              {' '}
              <h1 className="pt-16 text-2xl border-b border-ui">
                {t('dashboard_labels.referral_campaign')}
              </h1>
              <div className="grid grid-cols-2 gap-6">
                {campaignPerformance?.campaignPerformance?.referral?.length && (
                  <DashboardCard
                    title={t('dashboard_labels.campaign_overview')}
                    description={t(
                      'dashboard_labels.since_the_beginning_of_the_campaign',
                    )}
                    children={
                      <BarChart
                        data={generalToChart(
                          campaignPerformance?.campaignPerformance?.referral,
                        )}
                        type="campaign"
                      />
                    }
                  />
                )}
                {incentivesData?.incentivesPerCampaign?.referral && (
                  <DashboardCard
                    title={t('dashboard_labels.requested_incentives')}
                    description={t('dashboard_labels.by_type')}
                    children={
                      <BarChart
                        data={incentivesData?.incentivesPerCampaign?.referral}
                        type="incentive"
                      />
                    }
                  />
                )}
                {campaignPerformanceOverTime?.campaignPerformanceOverTime
                  .referral?.data && (
                  <DashboardCard
                    title={t('dashboard_labels.campaign_performance')}
                    description={t('dashboard_labels.over_time')}
                    children={
                      <LineChart
                        data={campaignPerformanceOverTime?.campaignPerformanceOverTime.referral.data?.map(
                          (row: any) => {
                            // Removed unused variable
                            if (!row) return { label: '', data: '' }
                            return {
                              label: t(
                                `dashboard_labels.performance_by_time.referral.${row.label}`,
                              ),
                              data: String(row.data?.[0] || ''),
                            }
                          },
                        )}
                        labels={
                          campaignPerformanceOverTime?.campaignPerformanceOverTime.referral.labels?.map(
                            (l: any) =>
                              (l && format(new Date(l), 'yyyy-MM-dd')) || '',
                          ) || []
                        }
                      />
                    }
                  />
                )}
                {incentivesData?.incentivesPerCampaign?.referral && (
                  <DashboardCard
                    title={t('dashboard_labels.requested_incentives')}
                    description={t('dashboard_labels.by_share')}
                    children={
                      <DoughnutChart
                        data={incentivesData?.incentivesPerCampaign?.referral}
                      />
                    }
                  />
                )}
              </div>
            </>
          )}
          {showHappinessCheck && (
            <div className="pt-16">
              {happinessCheckCount?.endUsersHappinessCheck ? (
                <DashboardCard
                  title={t('dashboard_labels.happiness_check') as any}
                  children={
                    <StackedBarChart
                      data={happinessCheckCount?.endUsersHappinessCheck as any}
                    />
                  }
                />
              ) : null}
            </div>
          )}
          {getVoucherStatistics.length > 0 && (
            <div className="pt-16">
              <DashboardCard
                title={t('dashboard_labels.incentive_code_used') as any}
                children={<BarChartHorizontal data={getVoucherStatistics} />}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function generalToChart(
  data: { action: string; count: number }[],
  totalEndUsers?: number,
  isOnlineCampaign?: boolean,
) {
  if (!data) return []
  const labels: Record<string, { order: number; label: string }> = {
    reached: {
      order: 1,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.scanned_qr_codes_online')
        : t('dashboard_labels.overview.scanned_qr_codes'),
    },
    questionnaireStarted: {
      order: 2,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.questionnaire_started_online')
        : t('dashboard_labels.overview.questionnaire_started'),
    },
    questionnaireCompleted: {
      order: 3,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.questionnaire_completed_online')
        : t('dashboard_labels.overview.questionnaire_completed'),
    },
    incentiveRequested: {
      order: 4,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.incentives_requested_online')
        : t('dashboard_labels.overview.incentives_requested'),
    },
  }

  const referralLabels: Record<string, { order: number; label: string }> = {
    reached: {
      order: 1,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.scanned_qr_codes_online')
        : t('dashboard_labels.overview.referral_link_opens'),
    },
    questionnaireStarted: {
      order: 2,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.questionnaire_started_online')
        : t('dashboard_labels.overview.questionnaire_started'),
    },
    questionnaireCompleted: {
      order: 3,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.questionnaire_completed_online')
        : t('dashboard_labels.overview.questionnaire_completed'),
    },
    incentiveRequested: {
      order: 4,
      label: isOnlineCampaign
        ? t('dashboard_labels.overview.incentives_requested_online')
        : t('dashboard_labels.overview.incentives_requested'),
    },
  }

  if (!totalEndUsers) {
    return [...data]
      .sort(
        (
          a: { action: string; count: number },
          b: { action: string; count: number },
        ) =>
          referralLabels[a?.action]?.order - referralLabels[b?.action]?.order,
      )
      .map((d: { action: string; count: number }) => ({
        label: referralLabels?.[d?.action]?.label,
        data: d?.count?.toString(),
      }))
  } else {
    return [
      {
        label: t('dashboard_labels.overview.campaign_reached'),
        data: totalEndUsers?.toString(),
      },
      ...[...data]
        .sort(
          (
            a: { action: string; count: number },
            b: { action: string; count: number },
          ) => labels?.[a?.action]?.order - labels?.[b?.action]?.order,
        )
        .map((d: { action: string; count: number }) => ({
          label: labels?.[d?.action]?.label,
          data: d?.count?.toString(),
        })),
    ]
  }
}
